/**
 * ChatMessageList — 群聊消息列表组件
 *
 * 功能：
 * - 按时间顺序展示消息
 * - 文本和图片消息差异化渲染
 * - 显示发送者头像、昵称、发送时间
 * - 消息发送状态（发送中/成功/失败）
 * - 自动滚动到底部
 */

import React, { useRef, useEffect } from 'react';
import type { GroupMessage } from '../../types';
import './index.scss';

export interface ChatMessageListProps {
  /** 消息列表 */
  messages: GroupMessage[];
  /** 当前用户 ID（用于区分自己的消息） */
  currentUserId: string;
  /** 重发失败消息 */
  onRetry?: (message: GroupMessage) => void;
}

/**
 * 格式化消息时间
 * @param timestamp 时间戳
 * @returns 格式化后的时间字符串
 */
const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');

  // 今天的消息只显示时分
  if (date.toDateString() === now.toDateString()) {
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  // 昨天的显示"昨天 时分"
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `昨天 ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  // 更早的显示月日时分
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * 获取头像显示内容
 * 有图显示图，无图显示昵称首字
 */
const getAvatarContent = (avatar: string, name: string): React.ReactNode => {
  if (avatar) {
    return <img src={avatar} alt={name} />;
  }
  return <span className="chat-message-avatar-text">{name.charAt(0).toUpperCase()}</span>;
};

/**
 * 消息列表组件
 */
const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  currentUserId,
  onRetry,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(messages.length);

  // 新消息到达时自动滚动到底部
  useEffect(() => {
    // 仅在消息数量增加时滚动
    if (messages.length > prevLengthRef.current) {
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
    prevLengthRef.current = messages.length;
  }, [messages.length]);

  // 首次加载时快速滚动到底部
  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, []);

  return (
    <div className="chat-message-list">
      {messages.length === 0 ? (
        <div className="chat-message-list-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <p>暂无消息，开始聊天吧</p>
        </div>
      ) : (
        messages.map((msg) => {
          const isSelf = msg.senderId === currentUserId;

          return (
            <div
              key={msg.id}
              className={`chat-message-item ${isSelf ? 'chat-message-self' : 'chat-message-other'}`}
            >
              {/* 对方消息显示头像 */}
              {!isSelf && (
                <div className="chat-message-avatar">
                  {getAvatarContent(msg.senderAvatar, msg.senderName)}
                </div>
              )}

              <div className="chat-message-body">
                {/* 对方昵称 */}
                {!isSelf && (
                  <div className="chat-message-sender">{msg.senderName}</div>
                )}

                {/* 消息气泡 — 支持 segments 混合排版 */}
                <div className="chat-message-bubble">
                  {msg.segments.map((seg, i) => (
                    seg.type === 'image' && seg.imageUrl ? (
                      <img key={i} src={seg.imageUrl} alt="图片消息" className="chat-message-image" />
                    ) : (
                      <p key={i} className="chat-message-text">{seg.content}</p>
                    )
                  ))}
                </div>

                {/* 消息状态 + 时间 */}
                <div className="chat-message-meta">
                  <span className="chat-message-time">{formatTime(msg.timestamp)}</span>
                  {/* 自己的消息显示状态 */}
                  {isSelf && (
                    <span className={`chat-message-status chat-message-status-${msg.status}`}>
                      {msg.status === 'sending' && '发送中...'}
                      {msg.status === 'sent' && ''}
                      {msg.status === 'failed' && (
                        <>
                          发送失败
                          {onRetry && (
                            <button
                              className="chat-message-retry"
                              onClick={() => onRetry(msg)}
                              aria-label="重发"
                            >
                              重发
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>

              {/* 自己的消息，头像在右侧 */}
              {isSelf && (
                <div className="chat-message-avatar">
                  {getAvatarContent(msg.senderAvatar, msg.senderName)}
                </div>
              )}
            </div>
          );
        })
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatMessageList;
